import { useEffect } from 'react';

/**
 * DPDPCRM Consent Widget Loader
 *
 * Loads the official drop-in widget from dpdpcrm.com and initialises it with
 * this site's template ID + publishable API key.
 *
 * The widget auto-derives its API base URL from the script src, so all
 * consent lifecycle calls (collect / status / withdraw) flow directly to
 * dpdpcrm.com — no backend hop.
 *
 * Our own backend proxy (/api/consent/*) is still used for:
 *   - Server-side audit logging in MongoDB (consent_log collection)
 *   - Form-level consent recording tied to specific channels
 *   - Evidence bundle fetching (requires consent.evidence scope — NOT exposed to browser)
 */
const DPDPConsentBoot = () => {
  useEffect(() => {
    const apiKey = process.env.REACT_APP_DPDP_API_KEY;
    const templateId = process.env.REACT_APP_DPDP_TEMPLATE_ID;
    const widgetUrl =
      process.env.REACT_APP_DPDP_WIDGET_URL ||
      'https://dpdpcrm.com/api/public/v1/consents/widget.js';

    if (!apiKey || !templateId) {
      console.warn('[DPDP] Missing REACT_APP_DPDP_API_KEY or REACT_APP_DPDP_TEMPLATE_ID');
      return;
    }

    // Avoid double-loading on hot reload / navigation
    if (window.__DPDP_WIDGET_LOADED__) {
      if (window.DPDPConsent) tryInit();
      return;
    }
    window.__DPDP_WIDGET_LOADED__ = true;

    const stableId = () => {
      let pid = localStorage.getItem('sipl_principal_id');
      if (!pid) {
        pid = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('sipl_principal_id', pid);
      }
      return pid;
    };

    function tryInit() {
      if (!window.DPDPConsent || window.__DPDP_WIDGET_INITIALISED__) return;
      window.__DPDP_WIDGET_INITIALISED__ = true;
      try {
        window.DPDPConsent.init({
          apiKey,
          templateId,
          principalId: stableId(),
          sourceApplication: 'sanghvi-website',
          theme: 'auto',
          autoShow: true,
          onReady: (state) => {
            // eslint-disable-next-line no-console
            console.info('[DPDP] widget ready', state);
          },
          onGranted: (consent) => {
            // eslint-disable-next-line no-console
            console.info('[DPDP] consent granted', consent);
            try {
              localStorage.setItem(
                'sipl_dpdp_consent_v1',
                JSON.stringify({
                  decision: 'accept',
                  consent_id: consent?.id,
                  recorded_at: new Date().toISOString(),
                })
              );
            } catch (e) {
              /* noop */
            }
            window.dispatchEvent(
              new CustomEvent('dpdp:granted', { detail: consent })
            );
          },
          onWithdrawn: (result) => {
            // eslint-disable-next-line no-console
            console.info('[DPDP] consent withdrawn', result);
            try {
              localStorage.removeItem('sipl_dpdp_consent_v1');
            } catch (e) {
              /* noop */
            }
            window.dispatchEvent(new CustomEvent('dpdp:withdrawn'));
          },
          onError: (err) => {
            // eslint-disable-next-line no-console
            console.warn('[DPDP] widget error', err);
          },
        });
      } catch (err) {
        console.error('[DPDP] widget init failed', err);
      }
    }

    const script = document.createElement('script');
    script.src = widgetUrl;
    script.async = true;
    script.onload = tryInit;
    script.onerror = () => {
      console.warn('[DPDP] Failed to load widget script from', widgetUrl);
    };
    document.body.appendChild(script);

    // cleanup only removes the script tag; widget itself stays mounted
    return () => {
      // Intentionally keep widget alive across route changes
    };
  }, []);

  return null;
};

export const openDPDPManage = () => {
  try {
    if (window.DPDPConsent && typeof window.DPDPConsent.open === 'function') {
      window.DPDPConsent.open('manage');
      return true;
    }
  } catch (e) {
    /* noop */
  }
  return false;
};

export default DPDPConsentBoot;
